import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatNativeDateModule } from '@angular/material/core';
import { AdminService } from '../../services/admin';
import { Excel } from '../../../../shared/utils/excel';
import { Partner } from '../../../../shared/models/partner';
import { Entity } from '../../../../shared/models/entity';
import { Route } from '../../../../shared/models/route';
import { Bus } from '../../../../shared/models/bus';

interface ReportDiscountOption {
    id: string;
    name: string;
}

interface GroupingOption {
    value: 'week' | 'entity' | 'route' | 'partner' | 'bus';
    label: string;
}

interface CatalogReportOption {
    value: 'tie' | 'plate' | 'route' | 'entity' | 'isr_affected' | 'bank_account' | 'owner_change' | 'nit';
    label: string;
}

interface BusLifecycleReportOption {
    value: 'activos' | 'altas' | 'inactivo' | 'traslados';
    label: string;
}

interface CatalogGroupingOption {
    value: 'partner' | 'entity' | 'route' | 'bus' | 'nit';
    label: string;
}

interface BusLifecycleGroupingOption {
    value: 'partner' | 'entity' | 'route' | 'bus';
    label: string;
}

type ReportFilterOption = Partner | Entity | Route | Bus | string;
type ReportFilterGrouping = GroupingOption['value'] | CatalogGroupingOption['value'] | BusLifecycleGroupingOption['value'];

interface LiquidationPaymentDiscount {
    name: string;
    amount: number;
}

interface LiquidationPaymentRow {
    nit: string;
    partnerName: string;
    entityName: string;
    routeName: string;
    buses: string;
    totalPassages: number;
    totalDiscounts: number;
    amountToDeposit: number;
    discounts: LiquidationPaymentDiscount[];
}

interface LiquidationPaymentReportData {
    rows: LiquidationPaymentRow[];
    discountNames: string[];
    summary: {
        totalPassagesBilling: number;
        totalToPay: number;
        totalToCollect: number;
    };
    metadata: {
        expandedStart: string;
        expandedEnd: string;
        years: string;
        weeks: string;
        entities: string;
    };
}

interface MissingDocumentsReportRow {
    partnerName: string;
    documentsPending: string;
    reason: string;
}

interface MissingDocumentsReportLiquidationBlock {
    liquidationId: string;
    liquidationLabel: string;
    isConsolidated?: boolean;
    mergedStatusText?: string;
    rows: MissingDocumentsReportRow[];
}

interface MissingDocumentsReportGroupBlock {
    groupKey: string;
    groupLabel: string;
    liquidations: MissingDocumentsReportLiquidationBlock[];
}

interface MissingDocumentsReportData {
    blocks: MissingDocumentsReportGroupBlock[];
}

interface RouteAccountStatementWeekRow {
    liquidationId: string;
    week: number;
    year: number;
    label: string;
    buses: string;
    facturacion: number;
    discounts: { name: string; amount: number }[];
    totalDiscounts: number;
    deposit: number;
    paid: number;
}

interface RouteAccountStatementPartner {
    partnerId: string;
    nit: string;
    partnerName: string;
    weeks: RouteAccountStatementWeekRow[];
}

interface RouteAccountStatementRoute {
    routeId: string;
    routeName: string;
    discountNames: string[];
    partners: RouteAccountStatementPartner[];
}

interface RouteAccountStatementReportData {
    routes: RouteAccountStatementRoute[];
}

@Component({
    selector: 'app-reports',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatChipsModule,
        MatCheckboxModule,
        MatIconModule,
        MatDatepickerModule,
        MatNativeDateModule
    ],
    templateUrl: './reports.html',
    styleUrl: './reports.css'
})
export class Reports implements OnInit {
    reportForm: FormGroup;
    depositReportForm: FormGroup;
    paymentReportForm: FormGroup;
    missingDocumentsReportForm: FormGroup;
    catalogReportForm: FormGroup;
    busLifecycleReportForm: FormGroup;
    discounts: ReportDiscountOption[] = [];
    groupingOptions: GroupingOption[] = [
        { value: 'week', label: 'Semana' },
        { value: 'entity', label: 'Entidad' },
        { value: 'route', label: 'Ruta' },
        { value: 'partner', label: 'Cliente' },
        { value: 'bus', label: 'Bus' }
    ];
    catalogReportOptions: CatalogReportOption[] = [
        { value: 'tie', label: 'TIE' },
        { value: 'plate', label: 'PLACA' },
        { value: 'route', label: 'RUTA' },
        { value: 'entity', label: 'ENTIDAD' },
        { value: 'isr_affected', label: 'AFECTOS DE ISR' },
        { value: 'bank_account', label: 'CUENTA BANCARIA' },
        { value: 'owner_change', label: 'CAMBIO DE PROPIETARIO' },
        { value: 'nit', label: 'NIT' }
    ];
    busLifecycleReportOptions: BusLifecycleReportOption[] = [
        { value: 'activos', label: 'ACTIVOS' },
        { value: 'altas', label: 'ALTAS' },
        { value: 'inactivo', label: 'INACTIVO' },
        { value: 'traslados', label: 'TRASLADOS' }
    ];
    catalogGroupingOptions: CatalogGroupingOption[] = [
        { value: 'partner', label: 'Cliente' },
        { value: 'entity', label: 'Entidad' },
        { value: 'route', label: 'Ruta' },
        { value: 'bus', label: 'Bus' },
        { value: 'nit', label: 'NIT' }
    ];
    busLifecycleGroupingOptions: BusLifecycleGroupingOption[] = [
        { value: 'partner', label: 'Cliente' },
        { value: 'entity', label: 'Entidad' },
        { value: 'route', label: 'Ruta' },
        { value: 'bus', label: 'Bus' }
    ];
    partners: Partner[] = [];
    entities: Entity[] = [];
    routes: Route[] = [];
    buses: Bus[] = [];
    nits: string[] = [];
    discountFilterSearchControl = new FormControl<string | null>(null);
    depositFilterSearchControl = new FormControl<string | null>(null);
    paymentFilterSearchControl = new FormControl<string | null>(null);
    missingDocumentsFilterSearchControl = new FormControl<string | null>(null);
    catalogFilterSearchControl = new FormControl<string | null>(null);
    busLifecycleFilterSearchControl = new FormControl<string | null>(null);
    discountFilterIds: string[] = [];
    depositFilterIds: string[] = [];
    paymentFilterIds: string[] = [];
    missingDocumentsFilterIds: string[] = [];
    catalogFilterIds: string[] = [];
    busLifecycleFilterIds: string[] = [];
    filteredDiscountFilterOptions: ReportFilterOption[] = [];
    filteredDepositFilterOptions: ReportFilterOption[] = [];
    filteredPaymentFilterOptions: ReportFilterOption[] = [];
    filteredMissingDocumentsFilterOptions: ReportFilterOption[] = [];
    filteredCatalogFilterOptions: ReportFilterOption[] = [];
    filteredBusLifecycleFilterOptions: ReportFilterOption[] = [];
    showDiscountFilterPanel = false;
    showDepositFilterPanel = false;
    showPaymentFilterPanel = false;
    showMissingDocumentsFilterPanel = false;
    showCatalogFilterPanel = false;
    showBusLifecycleFilterPanel = false;
    private partnerMap = new Map<string, Partner>();
    private entityMap = new Map<string, Entity>();
    private routeMap = new Map<string, Route>();
    private busMap = new Map<string, Bus>();
    isSubmittingDiscount = false;
    isSubmittingDeposit = false;
    isSubmittingPayment = false;
    isSubmittingMissingDocuments = false;
    isSubmittingCatalog = false;
    isSubmittingBusLifecycle = false;
    isSubmittingRouteAccountStatement = false;
    routeAccountStatementReportForm: FormGroup;
    routeAccountStatementYears: number[] = [];

    constructor(private fb: FormBuilder, private adminService: AdminService, private excelService: Excel) {
        this.reportForm = this.fb.group({
            discountId: [null, Validators.required],
            grouping: [null, Validators.required],
            startDate: [null, Validators.required],
            endDate: [null, Validators.required]
        }, { validators: this.validateDateRange });

        this.depositReportForm = this.fb.group({
            grouping: [null, Validators.required],
            startDate: [null, Validators.required],
            endDate: [null, Validators.required]
        }, { validators: this.validateDateRange });

        this.paymentReportForm = this.fb.group({
            grouping: [null, Validators.required],
            startDate: [null, Validators.required],
            endDate: [null, Validators.required]
        }, { validators: this.validateDateRange });

        this.missingDocumentsReportForm = this.fb.group({
            grouping: [null, Validators.required],
            startDate: [null, Validators.required],
            endDate: [null, Validators.required]
        }, { validators: this.validateDateRange });

        this.catalogReportForm = this.fb.group({
            reportType: [null, Validators.required],
            grouping: [null, Validators.required],
            startDate: [null],
            endDate: [null]
        }, { validators: this.validateCatalogDateRange });

        this.busLifecycleReportForm = this.fb.group({
            reportType: [null, Validators.required],
            grouping: [null, Validators.required],
            startDate: [null],
            endDate: [null]
        }, { validators: this.validateBusLifecycleDateRange });

        this.routeAccountStatementReportForm = this.fb.group({
            routeId: [null, Validators.required],
            year: [null, Validators.required]
        });
    }

    ngOnInit(): void {
        this.loadDiscounts();
        this.loadFilterCatalogs();
        this.loadRouteAccountStatementYears();
        this.bindFilterListeners();
    }

    private async loadRouteAccountStatementYears(): Promise<void> {
        const liquidations = await this.adminService.getAllLiquidations();
        this.routeAccountStatementYears = Array.from(new Set(
            (liquidations || []).map(liquidation => liquidation.year).filter(year => !!year)
        )).sort((a, b) => b - a);
    }

    private validateDateRange(form: FormGroup): null | { dateRangeInvalid: true } {
        const startDate = form.get('startDate')?.value as Date | null;
        const endDate = form.get('endDate')?.value as Date | null;

        if(!startDate || !endDate) {
            return null;
        }

        return startDate <= endDate ? null : { dateRangeInvalid: true };
    }

    private validateCatalogDateRange = (form: FormGroup): null | { dateRangeInvalid: true } | { datesRequired: true } => {
        const reportType = form.get('reportType')?.value as CatalogReportOption['value'] | null;
        if(reportType === 'nit') {
            return null;
        }

        const startDate = form.get('startDate')?.value as Date | null;
        const endDate = form.get('endDate')?.value as Date | null;
        const requiresDateRange = reportType === 'owner_change';

        if(requiresDateRange) {
            if(!startDate || !endDate) {
                return { datesRequired: true };
            }
            return startDate <= endDate ? null : { dateRangeInvalid: true };
        }

        if(!startDate && !endDate) {
            return null;
        }

        if(!startDate || !endDate) {
            return { datesRequired: true };
        }

        return startDate <= endDate ? null : { dateRangeInvalid: true };
    };

    private validateBusLifecycleDateRange = (form: FormGroup): null | { dateRangeInvalid: true } | { datesRequired: true } => {
        const reportType = form.get('reportType')?.value as BusLifecycleReportOption['value'] | null;
        const startDate = form.get('startDate')?.value as Date | null;
        const endDate = form.get('endDate')?.value as Date | null;
        const requiresDateRange = reportType === 'activos' || reportType === 'traslados';

        if(requiresDateRange) {
            if(!startDate || !endDate) {
                return { datesRequired: true };
            }
            return startDate <= endDate ? null : { dateRangeInvalid: true };
        }

        if(!startDate && !endDate) {
            return null;
        }

        if(!startDate || !endDate) {
            return { datesRequired: true };
        }

        return startDate <= endDate ? null : { dateRangeInvalid: true };
    };

    private async loadDiscounts(): Promise<void> {
        const result = await this.adminService.getAllDiscounts();

        if(result) {
            const baseDiscounts = result.map(d => ({ id: d.id, name: d.name }));
            this.discounts = [
                ...baseDiscounts,
                { id: 'ISR_GLOBAL', name: 'ISR' },
                { id: 'RETENTION_IVA', name: 'Retención IVA' }
            ];
        }
    }

    private async loadFilterCatalogs(): Promise<void> {
        const partnersResult = await this.adminService.getAllPartners();
        const entitiesResult = await this.adminService.getAllEntities();
        const routesResult   = await this.adminService.getAllRoutes();
        const busesResult    = await this.adminService.getAllBuses();

        this.partners = partnersResult || [];
        this.entities = entitiesResult || [];
        this.routes   = routesResult || [];
        this.buses    = busesResult || [];

        this.partnerMap = new Map(this.partners.map(partner => [partner.uid, partner]));
        this.entityMap  = new Map(this.entities.map(entity => [entity.id, entity]));
        this.routeMap   = new Map(this.routes.map(route => [route.id, route]));
        this.busMap     = new Map(this.buses.map(bus => [bus.id, bus]));
        this.nits       = Array.from(new Set(
            this.partners
                .map(partner => String(partner.NIT || '').trim())
                .filter(nit => !!nit)
        )).sort((a, b) => a.localeCompare(b));

        this.updateDiscountFilterOptions();
        this.updateDepositFilterOptions();
        this.updatePaymentFilterOptions();
        this.updateMissingDocumentsFilterOptions();
        this.updateCatalogFilterOptions();
        this.updateBusLifecycleFilterOptions();

        if((this.reportForm.get('grouping')?.value as GroupingOption['value'] | null) && this.discountFilterIds.length === 0) {
            this.selectAllDiscountFilters();
        }
        if((this.depositReportForm.get('grouping')?.value as GroupingOption['value'] | null) && this.depositFilterIds.length === 0) {
            this.selectAllDepositFilters();
        }
        if((this.paymentReportForm.get('grouping')?.value as GroupingOption['value'] | null) && this.paymentFilterIds.length === 0) {
            this.selectAllPaymentFilters();
        }
        if((this.missingDocumentsReportForm.get('grouping')?.value as GroupingOption['value'] | null) && this.missingDocumentsFilterIds.length === 0) {
            this.selectAllMissingDocumentsFilters();
        }
        if((this.catalogReportForm.get('grouping')?.value as CatalogGroupingOption['value'] | null) && this.catalogFilterIds.length === 0) {
            this.selectAllCatalogFilters();
        }
        if((this.busLifecycleReportForm.get('grouping')?.value as BusLifecycleGroupingOption['value'] | null) && this.busLifecycleFilterIds.length === 0) {
            this.selectAllBusLifecycleFilters();
        }
    }

    private bindFilterListeners(): void {
        const discountGroupingControl = this.reportForm.get('grouping');
        const depositGroupingControl = this.depositReportForm.get('grouping');
        const paymentGroupingControl = this.paymentReportForm.get('grouping');
        const missingDocumentsGroupingControl = this.missingDocumentsReportForm.get('grouping');
        const catalogGroupingControl = this.catalogReportForm.get('grouping');
        const busLifecycleGroupingControl = this.busLifecycleReportForm.get('grouping');
        const catalogReportTypeControl = this.catalogReportForm.get('reportType');
        const busLifecycleReportTypeControl = this.busLifecycleReportForm.get('reportType');

        if(discountGroupingControl) {
            discountGroupingControl.valueChanges.subscribe(() => {
                this.discountFilterIds = this.getDefaultFilterIdsForGrouping(this.reportForm.get('grouping')?.value as GroupingOption['value']);
                this.updateDiscountFilterOptions();
            });
        }

        if(depositGroupingControl) {
            depositGroupingControl.valueChanges.subscribe(() => {
                this.depositFilterIds = this.getDefaultFilterIdsForGrouping(this.depositReportForm.get('grouping')?.value as GroupingOption['value']);
                this.updateDepositFilterOptions();
            });
        }

        if(paymentGroupingControl) {
            paymentGroupingControl.valueChanges.subscribe(() => {
                this.paymentFilterIds = this.getDefaultFilterIdsForGrouping(this.paymentReportForm.get('grouping')?.value as GroupingOption['value']);
                this.updatePaymentFilterOptions();
            });
        }

        if(missingDocumentsGroupingControl) {
            missingDocumentsGroupingControl.valueChanges.subscribe(() => {
                this.missingDocumentsFilterIds = this.getDefaultFilterIdsForGrouping(this.missingDocumentsReportForm.get('grouping')?.value as GroupingOption['value']);
                this.updateMissingDocumentsFilterOptions();
            });
        }

        if(catalogGroupingControl) {
            catalogGroupingControl.valueChanges.subscribe(() => {
                this.catalogFilterIds = this.getDefaultFilterIdsForGrouping(this.catalogReportForm.get('grouping')?.value as CatalogGroupingOption['value']);
                this.updateCatalogFilterOptions();
            });
        }

        if(busLifecycleGroupingControl) {
            busLifecycleGroupingControl.valueChanges.subscribe(() => {
                this.busLifecycleFilterIds = this.getDefaultFilterIdsForGrouping(this.busLifecycleReportForm.get('grouping')?.value as BusLifecycleGroupingOption['value']);
                this.updateBusLifecycleFilterOptions();
            });
        }

        if(catalogReportTypeControl) {
            catalogReportTypeControl.valueChanges.subscribe((reportType: CatalogReportOption['value'] | null) => {
                const groupingControl = this.catalogReportForm.get('grouping');
                const startDateControl = this.catalogReportForm.get('startDate');
                const endDateControl = this.catalogReportForm.get('endDate');
                const isNit = reportType === 'nit';
                const requiresDateRange = reportType === 'owner_change';

                if(isNit) {
                    startDateControl?.clearValidators();
                    endDateControl?.clearValidators();
                    startDateControl?.setValue(null);
                    endDateControl?.setValue(null);
                } else {
                    if(requiresDateRange) {
                        startDateControl?.setValidators([Validators.required]);
                        endDateControl?.setValidators([Validators.required]);
                    } else {
                        startDateControl?.clearValidators();
                        endDateControl?.clearValidators();
                    }
                }

                startDateControl?.updateValueAndValidity({ emitEvent: false });
                endDateControl?.updateValueAndValidity({ emitEvent: false });

                if(groupingControl) {
                    const currentGrouping = groupingControl.value as CatalogGroupingOption['value'] | null;
                    if(!isNit && currentGrouping === 'nit') {
                        groupingControl.setValue(null);
                    }
                }

                this.catalogFilterIds = [];
                this.catalogFilterSearchControl.setValue(null);
                this.updateCatalogFilterOptions();
                this.catalogFilterIds = this.getDefaultFilterIdsForGrouping(this.catalogReportForm.get('grouping')?.value as CatalogGroupingOption['value']);
                this.catalogReportForm.updateValueAndValidity({ emitEvent: false });
            });
        }

        if(busLifecycleReportTypeControl) {
            busLifecycleReportTypeControl.valueChanges.subscribe((reportType: BusLifecycleReportOption['value'] | null) => {
                const startDateControl = this.busLifecycleReportForm.get('startDate');
                const endDateControl = this.busLifecycleReportForm.get('endDate');
                const requiresDateRange = reportType === 'activos' || reportType === 'traslados';

                if(requiresDateRange) {
                    startDateControl?.setValidators([Validators.required]);
                    endDateControl?.setValidators([Validators.required]);
                } else {
                    startDateControl?.clearValidators();
                    endDateControl?.clearValidators();
                }

                startDateControl?.updateValueAndValidity({ emitEvent: false });
                endDateControl?.updateValueAndValidity({ emitEvent: false });
                this.busLifecycleReportForm.updateValueAndValidity({ emitEvent: false });
            });
        }

        this.discountFilterSearchControl.valueChanges.subscribe(() => {
            this.updateDiscountFilterOptions();
        });

        this.depositFilterSearchControl.valueChanges.subscribe(() => {
            this.updateDepositFilterOptions();
        });

        this.paymentFilterSearchControl.valueChanges.subscribe(() => {
            this.updatePaymentFilterOptions();
        });

        this.missingDocumentsFilterSearchControl.valueChanges.subscribe(() => {
            this.updateMissingDocumentsFilterOptions();
        });

        this.catalogFilterSearchControl.valueChanges.subscribe(() => {
            this.updateCatalogFilterOptions();
        });

        this.busLifecycleFilterSearchControl.valueChanges.subscribe(() => {
            this.updateBusLifecycleFilterOptions();
        });
    }

    private getDefaultFilterIdsForGrouping(grouping: ReportFilterGrouping | null): string[] {
        if(!grouping || grouping === 'week') {
            return [];
        }

        return this.getFilterOptionsForGrouping(grouping)
            .map(option => this.getFilterOptionId(option, grouping))
            .filter(id => !!id);
    }

    private normalizeSelectedFilterIds(ids: string[] | null | undefined, grouping: ReportFilterGrouping | null): string[] {
        if(!grouping || grouping === 'week' || !Array.isArray(ids)) {
            return [];
        }

        const validIds = new Set(this.getDefaultFilterIdsForGrouping(grouping));
        return Array.from(new Set(ids.map(id => String(id || '').trim()).filter(id => !!id && validIds.has(id))));
    }

    private ensureValidFilterIds(currentIds: string[], grouping: ReportFilterGrouping | null): string[] {
        if(!grouping || grouping === 'week') {
            return [];
        }

        const validIds = new Set(this.getDefaultFilterIdsForGrouping(grouping));
        return (currentIds || []).filter(id => validIds.has(id));
    }

    async onGenerateReport(): Promise<void> {
        this.reportForm.markAllAsTouched();
        if(this.reportForm.invalid || this.isSubmittingDiscount) {
            return;
        }

        const discountId = this.reportForm.get('discountId')?.value as string;
        const grouping = this.reportForm.get('grouping')?.value as GroupingOption['value'];
        const startDate = this.reportForm.get('startDate')?.value as Date;
        const endDate = this.reportForm.get('endDate')?.value as Date;

        if(!discountId || !grouping || !startDate || !endDate) {
            return;
        }

        this.isSubmittingDiscount = true;
        const reportData = await this.adminService.generateDiscountReportExcel({
            discountId,
            grouping,
            startDate,
            endDate,
            ...this.buildFilterPayload(grouping, this.discountFilterIds)
        });
        this.isSubmittingDiscount = false;

        if(!reportData || reportData.length === 0) {
            return;
        }

        const selectedDiscount = this.discounts.find(d => d.id === discountId);
        const discountLabel = selectedDiscount?.name || discountId;
        const groupingLabel = this.groupingOptions.find(option => option.value === grouping)?.label || grouping;
        const formattedStart = this.formatDate(startDate);
        const formattedEnd = this.formatDate(endDate);

        const { displayHeaders, dataKeys, reportLabel } = this.getReportHeaders(grouping);

        const fileName = `Reporte_${this.cleanFileName(discountLabel)}_${grouping}_${formattedStart}_${formattedEnd}`;

        const detailsData: any[][] = [
            [reportLabel],
            ['Descuento:', discountLabel],
            ['Agrupación:', groupingLabel],
            ['Rango de fechas:', `${formattedStart} - ${formattedEnd}`]
        ];

        this.excelService.exportDataToExcel(
            reportData,
            displayHeaders,
            dataKeys,
            fileName,
            reportLabel,
            detailsData,
            'Detalles del Reporte',
            {
                currencyKeys: ['totalAmount'],
                negativeRedKeys: ['totalAmount']
            }
        );
    }

    async onGenerateLiquidationPaymentReport(): Promise<void> {
        this.paymentReportForm.markAllAsTouched();
        if(this.paymentReportForm.invalid || this.isSubmittingPayment) {
            return;
        }

        const grouping = this.paymentReportForm.get('grouping')?.value as GroupingOption['value'];
        const startDate = this.paymentReportForm.get('startDate')?.value as Date;
        const endDate = this.paymentReportForm.get('endDate')?.value as Date;

        if(!grouping || !startDate || !endDate) {
            return;
        }

        this.isSubmittingPayment = true;
        const reportPayload = await this.adminService.generateLiquidationPaymentReportExcel({
            grouping,
            startDate,
            endDate,
            ...this.buildFilterPayload(grouping, this.paymentFilterIds)
        }) as LiquidationPaymentReportData | null;
        this.isSubmittingPayment = false;

        if(!reportPayload || !Array.isArray(reportPayload.rows) || reportPayload.rows.length === 0) {
            return;
        }

        const discountNamesFromPayload = Array.isArray(reportPayload.discountNames)
            ? reportPayload.discountNames.filter(name => !!name)
            : [];
        const discountNames = discountNamesFromPayload.length > 0
            ? discountNamesFromPayload
            : Array.from(new Set(
                reportPayload.rows.flatMap(row => (Array.isArray(row.discounts) ? row.discounts.map(discount => discount.name) : []))
                    .filter(name => !!name)
            )).sort((a, b) => a.localeCompare(b));

        const discountColumns = discountNames.map(name => ({
            name,
            key: `discount_${this.cleanColumnName(name)}`
        }));

        const processedRows = reportPayload.rows.map((row, index) => {
            const rowDiscountMap = new Map<string, number>();
            (Array.isArray(row.discounts) ? row.discounts : []).forEach(discount => {
                const amount = Number(discount?.amount) || 0;
                rowDiscountMap.set(String(discount?.name || ''), amount);
            });

            const mappedRow: Record<string, string | number> = {
                index: index + 1,
                nit: row.nit || '',
                partnerName: row.partnerName || '',
                entityName: row.entityName || '',
                routeName: row.routeName || '',
                buses: row.buses || '',
                totalPassages: Number(row.totalPassages) || 0,
                totalDiscounts: Number(row.totalDiscounts) || 0,
                amountToDeposit: Number(row.amountToDeposit) || 0
            };

            discountColumns.forEach(col => {
                mappedRow[col.key] = rowDiscountMap.get(col.name) || 0;
            });

            return mappedRow;
        });

        const displayHeaders = [
            'No.',
            'Nit Cliente',
            'Cliente',
            'Entidad',
            'Ruta',
            'Autobuses',
            'Facturación Pasajes',
            ...discountColumns.map(col => col.name),
            'Total De Descuentos',
            'Depositar'
        ];

        const dataKeys = [
            'index',
            'nit',
            'partnerName',
            'entityName',
            'routeName',
            'buses',
            'totalPassages',
            ...discountColumns.map(col => col.key),
            'totalDiscounts',
            'amountToDeposit'
        ];

        const formattedStart = this.formatDate(startDate);
        const formattedEnd = this.formatDate(endDate);
        const fileName = `Reporte_Pago_Liquidacion_${grouping}_${formattedStart}_${formattedEnd}`;

        this.excelService.exportLiquidationPaymentReport(
            processedRows,
            displayHeaders,
            dataKeys,
            fileName,
            {
                expandedStart: reportPayload.metadata?.expandedStart || '',
                expandedEnd: reportPayload.metadata?.expandedEnd || '',
                years: reportPayload.metadata?.years || '',
                weeks: reportPayload.metadata?.weeks || '',
                entities: reportPayload.metadata?.entities || '',
                totalPassagesBilling: Number(reportPayload.summary?.totalPassagesBilling) || 0,
                totalToPay: Number(reportPayload.summary?.totalToPay) || 0,
                totalToCollect: Number(reportPayload.summary?.totalToCollect) || 0
            }
        );
    }

    async onGenerateRouteAccountStatementReport(): Promise<void> {
        this.routeAccountStatementReportForm.markAllAsTouched();
        if(this.routeAccountStatementReportForm.invalid || this.isSubmittingRouteAccountStatement) {
            return;
        }

        const routeId = this.routeAccountStatementReportForm.get('routeId')?.value as string;
        const year = this.routeAccountStatementReportForm.get('year')?.value as number;

        if(!routeId || !year) {
            return;
        }

        this.isSubmittingRouteAccountStatement = true;
        const reportPayload = await this.adminService.generateRouteAccountStatementReportExcel({ routeId, year }) as RouteAccountStatementReportData | null;
        this.isSubmittingRouteAccountStatement = false;

        if(!reportPayload || !Array.isArray(reportPayload.routes) || reportPayload.routes.length === 0) {
            return;
        }

        const fileName = `Estado_Cuenta_${routeId === 'all' ? 'Todas_Las_Rutas' : this.routeMap.get(routeId)?.name || routeId}_${year}`;
        await this.excelService.exportRouteAccountStatementReport(reportPayload.routes, fileName);
    }

    async onGenerateMissingDocumentsReport(): Promise<void> {
        this.missingDocumentsReportForm.markAllAsTouched();
        if(this.missingDocumentsReportForm.invalid || this.isSubmittingMissingDocuments) {
            return;
        }

        const grouping = this.missingDocumentsReportForm.get('grouping')?.value as GroupingOption['value'];
        const startDate = this.missingDocumentsReportForm.get('startDate')?.value as Date;
        const endDate = this.missingDocumentsReportForm.get('endDate')?.value as Date;

        if(!grouping || !startDate || !endDate) {
            return;
        }

        this.isSubmittingMissingDocuments = true;
        const reportPayload = await this.adminService.generateMissingDocumentsReportExcel({
            grouping,
            startDate,
            endDate,
            ...this.buildFilterPayload(grouping, this.missingDocumentsFilterIds)
        }) as MissingDocumentsReportData | null;
        this.isSubmittingMissingDocuments = false;

        if(!reportPayload || !Array.isArray(reportPayload.blocks) || reportPayload.blocks.length === 0) {
            return;
        }

        const groupingLabel = this.groupingOptions.find(option => option.value === grouping)?.label || grouping;
        const formattedStart = this.formatDate(startDate);
        const formattedEnd = this.formatDate(endDate);
        const fileName = `Reporte_Documentos_Faltantes_${grouping}_${formattedStart}_${formattedEnd}`;

        this.excelService.exportMissingDocumentsReport(
            reportPayload.blocks,
            fileName,
            groupingLabel,
            grouping === 'partner'
        );
    }

    async onGenerateDepositReport(): Promise<void> {
        this.depositReportForm.markAllAsTouched();
        if(this.depositReportForm.invalid || this.isSubmittingDeposit) {
            return;
        }

        const grouping = this.depositReportForm.get('grouping')?.value as GroupingOption['value'];
        const startDate = this.depositReportForm.get('startDate')?.value as Date;
        const endDate = this.depositReportForm.get('endDate')?.value as Date;

        if(!grouping || !startDate || !endDate) {
            return;
        }

        this.isSubmittingDeposit = true;
        const reportData = await this.adminService.generateDepositReportExcel({
            grouping,
            startDate,
            endDate,
            ...this.buildFilterPayload(grouping, this.depositFilterIds)
        });
        this.isSubmittingDeposit = false;

        if(!reportData || reportData.length === 0) {
            return;
        }

        const groupingLabel = this.groupingOptions.find(option => option.value === grouping)?.label || grouping;
        const formattedStart = this.formatDate(startDate);
        const formattedEnd = this.formatDate(endDate);

        const { displayHeaders, dataKeys, reportLabel } = this.getDepositReportHeaders(grouping);

        const fileName = `Reporte_Depositos_${grouping}_${formattedStart}_${formattedEnd}`;

        const detailsData: any[][] = [
            [reportLabel],
            ['Agrupación:', groupingLabel],
            ['Rango de fechas:', `${formattedStart} - ${formattedEnd}`]
        ];

        this.excelService.exportDataToExcel(
            reportData,
            displayHeaders,
            dataKeys,
            fileName,
            reportLabel,
            detailsData,
            'Detalles del Reporte',
            {
                currencyKeys: ['totalAmount'],
                negativeRedKeys: ['totalAmount']
            }
        );
    }

    async onGenerateCatalogReport(): Promise<void> {
        this.catalogReportForm.markAllAsTouched();
        if(this.catalogReportForm.invalid || this.isSubmittingCatalog) {
            return;
        }

        const reportType = this.catalogReportForm.get('reportType')?.value as CatalogReportOption['value'];
        const grouping = this.catalogReportForm.get('grouping')?.value as CatalogGroupingOption['value'];
        const startDate = this.catalogReportForm.get('startDate')?.value as Date | null;
        const endDate = this.catalogReportForm.get('endDate')?.value as Date | null;
        const isNitReport = reportType === 'nit';
        const requiresDateRange = reportType === 'owner_change';
        const hasDateRange = !!startDate && !!endDate;

        if(!reportType || !grouping) {
            return;
        }

        if(requiresDateRange && !hasDateRange) {
            return;
        }

        this.isSubmittingCatalog = true;
        const reportData = await this.adminService.generateCatalogSnapshotReportExcel({
            reportType,
            grouping,
            startDate: (isNitReport || !hasDateRange) ? null : startDate,
            endDate: (isNitReport || !hasDateRange) ? null : endDate,
            ...this.buildFilterPayload(grouping, this.catalogFilterIds)
        });
        this.isSubmittingCatalog = false;

        if(!reportData || reportData.length === 0) {
            return;
        }

        const reportLabel = this.catalogReportOptions.find(option => option.value === reportType)?.label || reportType;
        const groupingLabel = this.catalogGroupingOptions.find(option => option.value === grouping)?.label || grouping;
        const formattedStart = hasDateRange ? this.formatDate(startDate as Date) : '';
        const formattedEnd = hasDateRange ? this.formatDate(endDate as Date) : '';

        const { displayHeaders, dataKeys } = this.getCatalogReportHeaders(reportType, hasDateRange);

        const fileName = isNitReport
            ? `Reporte_Clientes_${this.cleanFileName(reportLabel)}`
            : hasDateRange
                ? `Reporte_Clientes_${this.cleanFileName(reportLabel)}_${formattedStart}_${formattedEnd}`
                : `Reporte_Clientes_${this.cleanFileName(reportLabel)}_actual`;

        const detailsData: any[][] = [
            ['Reporte Clientes'],
            ['Tipo de reporte:', reportLabel],
            ['Agrupación:', groupingLabel]
        ];
        if(!isNitReport && hasDateRange) {
            detailsData.push(['Rango de fechas:', `${formattedStart} - ${formattedEnd}`]);
        }

        this.excelService.exportDataToExcel(
            reportData,
            displayHeaders,
            dataKeys,
            fileName,
            'Reporte Clientes',
            detailsData,
            'Detalles del Reporte',
            {
                applyBlackBorders: true,
                centerHeaders: true,
                centerNumericValues: true,
                leftAlignTextValues: true
            }
        );
    }

    async onGenerateBusLifecycleReport(): Promise<void> {
        this.busLifecycleReportForm.markAllAsTouched();
        if(this.busLifecycleReportForm.invalid || this.isSubmittingBusLifecycle) {
            return;
        }

        const reportType = this.busLifecycleReportForm.get('reportType')?.value as BusLifecycleReportOption['value'];
        const grouping = this.busLifecycleReportForm.get('grouping')?.value as BusLifecycleGroupingOption['value'];
        const startDate = this.busLifecycleReportForm.get('startDate')?.value as Date | null;
        const endDate = this.busLifecycleReportForm.get('endDate')?.value as Date | null;
        const requiresDateRange = reportType === 'activos' || reportType === 'traslados';
        const hasDateRange = !!startDate && !!endDate;

        if(!reportType || !grouping) {
            return;
        }

        if(requiresDateRange && !hasDateRange) {
            return;
        }

        this.isSubmittingBusLifecycle = true;
        const reportData = await this.adminService.generateBusLifecycleReportExcel({
            reportType,
            grouping,
            startDate: hasDateRange ? startDate : null,
            endDate: hasDateRange ? endDate : null,
            ...this.buildFilterPayload(grouping, this.busLifecycleFilterIds)
        });
        this.isSubmittingBusLifecycle = false;

        if(!reportData || reportData.length === 0) {
            return;
        }

        const reportLabel = this.busLifecycleReportOptions.find(option => option.value === reportType)?.label || reportType;
        const groupingLabel = this.busLifecycleGroupingOptions.find(option => option.value === grouping)?.label || grouping;
        const formattedStart = hasDateRange ? this.formatDate(startDate as Date) : '';
        const formattedEnd = hasDateRange ? this.formatDate(endDate as Date) : '';

        const { displayHeaders, dataKeys } = this.getBusLifecycleReportHeaders(reportType, reportData, hasDateRange);

        const fileName = hasDateRange
            ? `Reporte_Autobus_${this.cleanFileName(reportLabel)}_${formattedStart}_${formattedEnd}`
            : `Reporte_Autobus_${this.cleanFileName(reportLabel)}_actual`;

        const detailsData: any[][] = [
            ['Reporte Autobus'],
            ['Tipo de reporte:', reportLabel],
            ['Agrupación:', groupingLabel]
        ];

        if(hasDateRange) {
            detailsData.push(['Rango de fechas:', `${formattedStart} - ${formattedEnd}`]);
        }

        const busLifecycleExportOptions: {
            appendTotalsRow?: boolean;
            totalsKeys?: Record<string, 'sum'>;
            applyBlackBorders: boolean;
            centerHeaders: boolean;
            centerNumericValues: boolean;
            leftAlignTextValues: boolean;
            totalsLabel?: string;
            totalsLabelMergeSpan?: number;
        } = {
            applyBlackBorders: true,
            centerHeaders: true,
            centerNumericValues: true,
            leftAlignTextValues: true
        };

        if(reportType === 'activos') {
            const totalsKeys = dataKeys.reduce((acc: Record<string, 'sum'>, key) => {
                if(key.startsWith('week_')) {
                    acc[key] = 'sum';
                }
                return acc;
            }, {});

            busLifecycleExportOptions.appendTotalsRow = true;
            busLifecycleExportOptions.totalsKeys = totalsKeys;
            busLifecycleExportOptions.totalsLabel = 'Totales';
            busLifecycleExportOptions.totalsLabelMergeSpan = 3;
        }

        this.excelService.exportDataToExcel(
            reportData,
            displayHeaders,
            dataKeys,
            fileName,
            'Reporte Autobus',
            detailsData,
            'Detalles del Reporte',
            busLifecycleExportOptions
        );
    }

    addDiscountFilter(event: MatAutocompleteSelectedEvent): void {
        const grouping = this.reportForm.get('grouping')?.value as GroupingOption['value'];
        if(!grouping || grouping === 'week') {
            return;
        }

        const selectedItem = event.option.value;
        const idToAdd = this.getFilterOptionId(selectedItem, grouping);
        if(!idToAdd) {
            return;
        }
        if(!this.discountFilterIds.includes(idToAdd)) {
            this.discountFilterIds.push(idToAdd);
        }
        this.discountFilterSearchControl.setValue(null);
        this.updateDiscountFilterOptions();
    }

    removeDiscountFilter(id: string): void {
        this.discountFilterIds = this.discountFilterIds.filter(entry => entry !== id);
        this.updateDiscountFilterOptions();
    }

    addDepositFilter(event: MatAutocompleteSelectedEvent): void {
        const grouping = this.depositReportForm.get('grouping')?.value as GroupingOption['value'];
        if(!grouping || grouping === 'week') {
            return;
        }

        const selectedItem = event.option.value;
        const idToAdd = this.getFilterOptionId(selectedItem, grouping);
        if(!idToAdd) {
            return;
        }
        if(!this.depositFilterIds.includes(idToAdd)) {
            this.depositFilterIds.push(idToAdd);
        }
        this.depositFilterSearchControl.setValue(null);
        this.updateDepositFilterOptions();
    }

    removeDepositFilter(id: string): void {
        this.depositFilterIds = this.depositFilterIds.filter(entry => entry !== id);
        this.updateDepositFilterOptions();
    }

    addPaymentFilter(event: MatAutocompleteSelectedEvent): void {
        const grouping = this.paymentReportForm.get('grouping')?.value as GroupingOption['value'];
        if(!grouping || grouping === 'week') {
            return;
        }

        const selectedItem = event.option.value;
        const idToAdd = this.getFilterOptionId(selectedItem, grouping);
        if(!idToAdd) {
            return;
        }
        if(!this.paymentFilterIds.includes(idToAdd)) {
            this.paymentFilterIds.push(idToAdd);
        }
        this.paymentFilterSearchControl.setValue(null);
        this.updatePaymentFilterOptions();
    }

    removePaymentFilter(id: string): void {
        this.paymentFilterIds = this.paymentFilterIds.filter(entry => entry !== id);
        this.updatePaymentFilterOptions();
    }

    addMissingDocumentsFilter(event: MatAutocompleteSelectedEvent): void {
        const grouping = this.missingDocumentsReportForm.get('grouping')?.value as GroupingOption['value'];
        if(!grouping || grouping === 'week') {
            return;
        }

        const selectedItem = event.option.value;
        const idToAdd = this.getFilterOptionId(selectedItem, grouping);
        if(!idToAdd) {
            return;
        }
        if(!this.missingDocumentsFilterIds.includes(idToAdd)) {
            this.missingDocumentsFilterIds.push(idToAdd);
        }
        this.missingDocumentsFilterSearchControl.setValue(null);
        this.updateMissingDocumentsFilterOptions();
    }

    removeMissingDocumentsFilter(id: string): void {
        this.missingDocumentsFilterIds = this.missingDocumentsFilterIds.filter(entry => entry !== id);
        this.updateMissingDocumentsFilterOptions();
    }

    addCatalogFilter(event: MatAutocompleteSelectedEvent): void {
        const grouping = this.catalogReportForm.get('grouping')?.value as CatalogGroupingOption['value'];
        if(!grouping) {
            return;
        }

        const selectedItem = event.option.value as ReportFilterOption;
        const idToAdd = this.getFilterOptionId(selectedItem, grouping);
        if(!idToAdd) {
            return;
        }
        if(!this.catalogFilterIds.includes(idToAdd)) {
            this.catalogFilterIds.push(idToAdd);
        }
        this.catalogFilterSearchControl.setValue(null);
        this.updateCatalogFilterOptions();
    }

    removeCatalogFilter(id: string): void {
        this.catalogFilterIds = this.catalogFilterIds.filter(entry => entry !== id);
        this.updateCatalogFilterOptions();
    }

    addBusLifecycleFilter(event: MatAutocompleteSelectedEvent): void {
        const grouping = this.busLifecycleReportForm.get('grouping')?.value as BusLifecycleGroupingOption['value'];
        if(!grouping) {
            return;
        }

        const selectedItem = event.option.value as ReportFilterOption;
        const idToAdd = this.getFilterOptionId(selectedItem, grouping);
        if(!idToAdd) {
            return;
        }
        if(!this.busLifecycleFilterIds.includes(idToAdd)) {
            this.busLifecycleFilterIds.push(idToAdd);
        }
        this.busLifecycleFilterSearchControl.setValue(null);
        this.updateBusLifecycleFilterOptions();
    }

    removeBusLifecycleFilter(id: string): void {
        this.busLifecycleFilterIds = this.busLifecycleFilterIds.filter(entry => entry !== id);
        this.updateBusLifecycleFilterOptions();
    }

    toggleDiscountFilterPanel(): void {
        this.showDiscountFilterPanel = !this.showDiscountFilterPanel;
    }

    toggleDepositFilterPanel(): void {
        this.showDepositFilterPanel = !this.showDepositFilterPanel;
    }

    togglePaymentFilterPanel(): void {
        this.showPaymentFilterPanel = !this.showPaymentFilterPanel;
    }

    toggleMissingDocumentsFilterPanel(): void {
        this.showMissingDocumentsFilterPanel = !this.showMissingDocumentsFilterPanel;
    }

    toggleCatalogFilterPanel(): void {
        this.showCatalogFilterPanel = !this.showCatalogFilterPanel;
    }

    toggleBusLifecycleFilterPanel(): void {
        this.showBusLifecycleFilterPanel = !this.showBusLifecycleFilterPanel;
    }

    displayDiscountFilterOption = (option: Partner | Entity | Route | Bus | string | null): string => {
        const grouping = this.reportForm.get('grouping')?.value as GroupingOption['value'];
        return this.getFilterOptionLabel(option, grouping);
    };

    displayDepositFilterOption = (option: Partner | Entity | Route | Bus | string | null): string => {
        const grouping = this.depositReportForm.get('grouping')?.value as GroupingOption['value'];
        return this.getFilterOptionLabel(option, grouping);
    };

    displayPaymentFilterOption = (option: Partner | Entity | Route | Bus | string | null): string => {
        const grouping = this.paymentReportForm.get('grouping')?.value as GroupingOption['value'];
        return this.getFilterOptionLabel(option, grouping);
    };

    displayMissingDocumentsFilterOption = (option: Partner | Entity | Route | Bus | string | null): string => {
        const grouping = this.missingDocumentsReportForm.get('grouping')?.value as GroupingOption['value'];
        return this.getFilterOptionLabel(option, grouping);
    };

    displayCatalogFilterOption = (option: ReportFilterOption | null): string => {
        const grouping = this.catalogReportForm.get('grouping')?.value as CatalogGroupingOption['value'];
        return this.getFilterOptionLabel(option, grouping);
    };

    displayBusLifecycleFilterOption = (option: ReportFilterOption | null): string => {
        const grouping = this.busLifecycleReportForm.get('grouping')?.value as BusLifecycleGroupingOption['value'];
        return this.getFilterOptionLabel(option, grouping);
    };

    getCatalogGroupingOptions(): CatalogGroupingOption[] {
        const reportType = this.catalogReportForm.get('reportType')?.value as CatalogReportOption['value'] | null;
        const includeNit = reportType === 'nit';
        return this.catalogGroupingOptions.filter(option => includeNit || option.value !== 'nit');
    }

    getFilterLabel(grouping: ReportFilterGrouping | null): string {
        if(!grouping) {
            return '';
        }
        if(grouping === 'nit') {
            return 'NIT';
        }
        const baseOption = this.groupingOptions.find(item => item.value === grouping);
        if(baseOption) {
            return baseOption.label;
        }
        const catalogOption = this.catalogGroupingOptions.find(item => item.value === grouping);
        return catalogOption?.label || '';
    }

    getFilterLabelPlural(grouping: ReportFilterGrouping | null): string {
        if(grouping === 'partner') return 'clientes';
        if(grouping === 'entity') return 'entidades';
        if(grouping === 'route') return 'rutas';
        if(grouping === 'bus') return 'buses';
        if(grouping === 'nit') return 'nits';
        return '';
    }

    getFilterPlaceholder(grouping: ReportFilterGrouping | null): string {
        if(grouping === 'partner') return 'Buscar y añadir clientes...';
        if(grouping === 'entity') return 'Buscar y añadir entidades...';
        if(grouping === 'route') return 'Buscar y añadir rutas...';
        if(grouping === 'bus') return 'Buscar y añadir buses...';
        if(grouping === 'nit') return 'Buscar y añadir NIT...';
        return '';
    }

    getSelectedFilterLabel(id: string, grouping: ReportFilterGrouping | null): string {
        if(!id || !grouping) {
            return id || '';
        }

        if(grouping === 'partner') {
            const partner = this.partnerMap.get(id);
            return partner ? (partner.displayName || partner.email || id) : id;
        }
        if(grouping === 'entity') {
            const entity = this.entityMap.get(id);
            return entity ? (entity.name || id) : id;
        }
        if(grouping === 'route') {
            const route = this.routeMap.get(id);
            return route ? (route.name || id) : id;
        }
        if(grouping === 'bus') {
            const bus = this.busMap.get(id);
            if(!bus) {
                return id;
            }
            const busName = bus.name || id;
            const plate = bus.licensePlate || 'N/A';
            return `${busName} (${plate})`;
        }
        if(grouping === 'nit') {
            return id;
        }

        return id;
    }

    getFilterOptionLabel(option: ReportFilterOption | null, grouping: ReportFilterGrouping | null): string {
        if(!option || !grouping) {
            return '';
        }

        if(typeof option === 'string') {
            return this.getSelectedFilterLabel(option, grouping);
        }

        if(grouping === 'partner') {
            const partner = option as Partner;
            return partner.displayName || partner.email || '';
        }
        if(grouping === 'entity') {
            return (option as Entity).name || '';
        }
        if(grouping === 'route') {
            return (option as Route).name || '';
        }
        if(grouping === 'bus') {
            const bus = option as Bus;
            const name = bus.name || bus.id || '';
            const plate = bus.licensePlate || 'N/A';
            return `${name} (${plate})`;
        }
        if(grouping === 'nit') {
            return String(option || '');
        }

        return '';
    }

    private getFilterOptionId(option: ReportFilterOption, grouping: ReportFilterGrouping | null): string {
        if(!option || !grouping) {
            return '';
        }
        if(grouping === 'nit') {
            return String(option || '').trim();
        }
        if(grouping === 'partner') {
            return (option as Partner).uid || '';
        }
        if(grouping === 'entity') {
            return (option as Entity).id || '';
        }
        if(grouping === 'route') {
            return (option as Route).id || '';
        }
        if(grouping === 'bus') {
            return (option as Bus).id || '';
        }
        return '';
    }

    private getFilterOptionsForGrouping(grouping: ReportFilterGrouping | null): ReportFilterOption[] {
        if(!grouping || grouping === 'week') {
            return [];
        }
        if(grouping === 'partner') return this.partners;
        if(grouping === 'entity') return this.entities;
        if(grouping === 'route') return this.routes;
        if(grouping === 'bus') return this.buses;
        if(grouping === 'nit') return this.nits;
        return [];
    }

    private updateDiscountFilterOptions(): void {
        const grouping = this.reportForm.get('grouping')?.value as GroupingOption['value'];
        this.filteredDiscountFilterOptions = this.getFilterOptionsForGrouping(grouping);
        this.discountFilterIds = this.ensureValidFilterIds(this.discountFilterIds, grouping);
    }

    private updateDepositFilterOptions(): void {
        const grouping = this.depositReportForm.get('grouping')?.value as GroupingOption['value'];
        this.filteredDepositFilterOptions = this.getFilterOptionsForGrouping(grouping);
        this.depositFilterIds = this.ensureValidFilterIds(this.depositFilterIds, grouping);
    }

    private updatePaymentFilterOptions(): void {
        const grouping = this.paymentReportForm.get('grouping')?.value as GroupingOption['value'];
        this.filteredPaymentFilterOptions = this.getFilterOptionsForGrouping(grouping);
        this.paymentFilterIds = this.ensureValidFilterIds(this.paymentFilterIds, grouping);
    }

    private updateMissingDocumentsFilterOptions(): void {
        const grouping = this.missingDocumentsReportForm.get('grouping')?.value as GroupingOption['value'];
        this.filteredMissingDocumentsFilterOptions = this.getFilterOptionsForGrouping(grouping);
        this.missingDocumentsFilterIds = this.ensureValidFilterIds(this.missingDocumentsFilterIds, grouping);
    }

    private updateCatalogFilterOptions(): void {
        const grouping = this.catalogReportForm.get('grouping')?.value as CatalogGroupingOption['value'];
        this.filteredCatalogFilterOptions = this.getFilterOptionsForGrouping(grouping);
        this.catalogFilterIds = this.ensureValidFilterIds(this.catalogFilterIds, grouping);
    }

    private updateBusLifecycleFilterOptions(): void {
        const grouping = this.busLifecycleReportForm.get('grouping')?.value as BusLifecycleGroupingOption['value'];
        this.filteredBusLifecycleFilterOptions = this.getFilterOptionsForGrouping(grouping);
        this.busLifecycleFilterIds = this.ensureValidFilterIds(this.busLifecycleFilterIds, grouping);
    }

    private buildFilterPayload(grouping: ReportFilterGrouping, filterIds: string[]): { filterType?: string; filterIds?: string[] } {
        if(grouping === 'week' || !Array.isArray(filterIds) || filterIds.length === 0) {
            return {};
        }
        return {
            filterType: grouping,
            filterIds: filterIds
        };
    }

    getFilterOptionValue(option: ReportFilterOption, grouping: ReportFilterGrouping | null): string {
        return this.getFilterOptionId(option, grouping);
    }

    getSelectionCountLabel(filterIds: string[]): string {
        const count = Array.isArray(filterIds) ? filterIds.length : 0;
        if(count === 1) {
            return '1 ítem seleccionado';
        }
        return `${count} ítems seleccionados`;
    }

    onDiscountFilterSelectionChange(selectedIds: string[] | null): void {
        const grouping = this.reportForm.get('grouping')?.value as GroupingOption['value'];
        this.discountFilterIds = this.normalizeSelectedFilterIds(selectedIds, grouping);
    }

    selectAllDiscountFilters(): void {
        const grouping = this.reportForm.get('grouping')?.value as GroupingOption['value'];
        this.discountFilterIds = this.getDefaultFilterIdsForGrouping(grouping);
    }

    clearDiscountFilters(): void {
        this.discountFilterIds = [];
    }

    isAllDiscountFiltersSelected(): boolean {
        const grouping = this.reportForm.get('grouping')?.value as GroupingOption['value'];
        const allIds = this.getDefaultFilterIdsForGrouping(grouping);
        return allIds.length > 0 && this.discountFilterIds.length === allIds.length;
    }

    onDepositFilterSelectionChange(selectedIds: string[] | null): void {
        const grouping = this.depositReportForm.get('grouping')?.value as GroupingOption['value'];
        this.depositFilterIds = this.normalizeSelectedFilterIds(selectedIds, grouping);
    }

    selectAllDepositFilters(): void {
        const grouping = this.depositReportForm.get('grouping')?.value as GroupingOption['value'];
        this.depositFilterIds = this.getDefaultFilterIdsForGrouping(grouping);
    }

    clearDepositFilters(): void {
        this.depositFilterIds = [];
    }

    isAllDepositFiltersSelected(): boolean {
        const grouping = this.depositReportForm.get('grouping')?.value as GroupingOption['value'];
        const allIds = this.getDefaultFilterIdsForGrouping(grouping);
        return allIds.length > 0 && this.depositFilterIds.length === allIds.length;
    }

    onPaymentFilterSelectionChange(selectedIds: string[] | null): void {
        const grouping = this.paymentReportForm.get('grouping')?.value as GroupingOption['value'];
        this.paymentFilterIds = this.normalizeSelectedFilterIds(selectedIds, grouping);
    }

    selectAllPaymentFilters(): void {
        const grouping = this.paymentReportForm.get('grouping')?.value as GroupingOption['value'];
        this.paymentFilterIds = this.getDefaultFilterIdsForGrouping(grouping);
    }

    clearPaymentFilters(): void {
        this.paymentFilterIds = [];
    }

    isAllPaymentFiltersSelected(): boolean {
        const grouping = this.paymentReportForm.get('grouping')?.value as GroupingOption['value'];
        const allIds = this.getDefaultFilterIdsForGrouping(grouping);
        return allIds.length > 0 && this.paymentFilterIds.length === allIds.length;
    }

    onMissingDocumentsFilterSelectionChange(selectedIds: string[] | null): void {
        const grouping = this.missingDocumentsReportForm.get('grouping')?.value as GroupingOption['value'];
        this.missingDocumentsFilterIds = this.normalizeSelectedFilterIds(selectedIds, grouping);
    }

    selectAllMissingDocumentsFilters(): void {
        const grouping = this.missingDocumentsReportForm.get('grouping')?.value as GroupingOption['value'];
        this.missingDocumentsFilterIds = this.getDefaultFilterIdsForGrouping(grouping);
    }

    clearMissingDocumentsFilters(): void {
        this.missingDocumentsFilterIds = [];
    }

    isAllMissingDocumentsFiltersSelected(): boolean {
        const grouping = this.missingDocumentsReportForm.get('grouping')?.value as GroupingOption['value'];
        const allIds = this.getDefaultFilterIdsForGrouping(grouping);
        return allIds.length > 0 && this.missingDocumentsFilterIds.length === allIds.length;
    }

    onCatalogFilterSelectionChange(selectedIds: string[] | null): void {
        const grouping = this.catalogReportForm.get('grouping')?.value as CatalogGroupingOption['value'];
        this.catalogFilterIds = this.normalizeSelectedFilterIds(selectedIds, grouping);
    }

    selectAllCatalogFilters(): void {
        const grouping = this.catalogReportForm.get('grouping')?.value as CatalogGroupingOption['value'];
        this.catalogFilterIds = this.getDefaultFilterIdsForGrouping(grouping);
    }

    clearCatalogFilters(): void {
        this.catalogFilterIds = [];
    }

    isAllCatalogFiltersSelected(): boolean {
        const grouping = this.catalogReportForm.get('grouping')?.value as CatalogGroupingOption['value'];
        const allIds = this.getDefaultFilterIdsForGrouping(grouping);
        return allIds.length > 0 && this.catalogFilterIds.length === allIds.length;
    }

    onBusLifecycleFilterSelectionChange(selectedIds: string[] | null): void {
        const grouping = this.busLifecycleReportForm.get('grouping')?.value as BusLifecycleGroupingOption['value'];
        this.busLifecycleFilterIds = this.normalizeSelectedFilterIds(selectedIds, grouping);
    }

    selectAllBusLifecycleFilters(): void {
        const grouping = this.busLifecycleReportForm.get('grouping')?.value as BusLifecycleGroupingOption['value'];
        this.busLifecycleFilterIds = this.getDefaultFilterIdsForGrouping(grouping);
    }

    clearBusLifecycleFilters(): void {
        this.busLifecycleFilterIds = [];
    }

    isAllBusLifecycleFiltersSelected(): boolean {
        const grouping = this.busLifecycleReportForm.get('grouping')?.value as BusLifecycleGroupingOption['value'];
        const allIds = this.getDefaultFilterIdsForGrouping(grouping);
        return allIds.length > 0 && this.busLifecycleFilterIds.length === allIds.length;
    }

    private getReportHeaders(grouping: GroupingOption['value']): { displayHeaders: string[]; dataKeys: string[]; reportLabel: string } {
        switch(grouping) {
            case 'week':
                return {
                    reportLabel: 'Reporte por Semana',
                    displayHeaders: ['Semana', 'Año', 'Rango', 'Días considerados', 'Total descuento (Q)'],
                    dataKeys: ['week', 'year', 'dateRange', 'daysCount', 'totalAmount']
                };
            case 'entity':
                return {
                    reportLabel: 'Reporte por Entidad',
                    displayHeaders: ['Entidad', 'Total descuento (Q)'],
                    dataKeys: ['entityName', 'totalAmount']
                };
            case 'route':
                return {
                    reportLabel: 'Reporte por Ruta',
                    displayHeaders: ['Ruta', 'Total descuento (Q)'],
                    dataKeys: ['routeName', 'totalAmount']
                };
            case 'partner':
                return {
                    reportLabel: 'Reporte por Cliente',
                    displayHeaders: ['Cliente', 'Email', 'Total descuento (Q)'],
                    dataKeys: ['partnerName', 'partnerEmail', 'totalAmount']
                };
            case 'bus':
            default:
                return {
                    reportLabel: 'Reporte por Bus',
                    displayHeaders: ['Bus', 'Ruta', 'Entidad', 'Cliente', 'Total descuento (Q)'],
                    dataKeys: ['busName', 'routeName', 'entityName', 'partnerName', 'totalAmount']
                };
        }
    }

    private getDepositReportHeaders(grouping: GroupingOption['value']): { displayHeaders: string[]; dataKeys: string[]; reportLabel: string } {
        switch(grouping) {
            case 'week':
                return {
                    reportLabel: 'Reporte por Semana',
                    displayHeaders: ['Semana', 'Año', 'Rango', 'Días considerados', 'Monto a depositar (Q)'],
                    dataKeys: ['week', 'year', 'dateRange', 'daysCount', 'totalAmount']
                };
            case 'entity':
                return {
                    reportLabel: 'Reporte por Entidad',
                    displayHeaders: ['Entidad', 'Monto a depositar (Q)'],
                    dataKeys: ['entityName', 'totalAmount']
                };
            case 'route':
                return {
                    reportLabel: 'Reporte por Ruta',
                    displayHeaders: ['Ruta', 'Monto a depositar (Q)'],
                    dataKeys: ['routeName', 'totalAmount']
                };
            case 'partner':
                return {
                    reportLabel: 'Reporte por Cliente',
                    displayHeaders: ['Cliente', 'Email', 'Monto a depositar (Q)'],
                    dataKeys: ['partnerName', 'partnerEmail', 'totalAmount']
                };
            case 'bus':
            default:
                return {
                    reportLabel: 'Reporte por Bus',
                    displayHeaders: ['Bus', 'Ruta', 'Entidad', 'Cliente', 'Monto a depositar (Q)'],
                    dataKeys: ['busName', 'routeName', 'entityName', 'partnerName', 'totalAmount']
                };
        }
    }

    private getCatalogReportHeaders(reportType: CatalogReportOption['value'], hasDateRange: boolean): { displayHeaders: string[]; dataKeys: string[] } {
        switch(reportType) {
            case 'tie':
                return hasDateRange
                    ? { displayHeaders: ['No.', 'TIE', 'CLIENTE', 'ENTIDAD', 'FECHA DE ASIGNACIÓN'], dataKeys: ['index', 'tie', 'name', 'entity', 'assignedAt'] }
                    : { displayHeaders: ['No.', 'TIE', 'CLIENTE', 'ENTIDAD'], dataKeys: ['index', 'tie', 'name', 'entity'] };
            case 'plate':
                return hasDateRange
                    ? { displayHeaders: ['No.', 'PLACA', 'TIE', 'CLIENTE', 'FECHA DE ASIGNACIÓN', 'ENTIDAD'], dataKeys: ['index', 'plate', 'tie', 'name', 'assignedAt', 'entity'] }
                    : { displayHeaders: ['No.', 'PLACA', 'TIE', 'CLIENTE', 'ENTIDAD'], dataKeys: ['index', 'plate', 'tie', 'name', 'entity'] };
            case 'route':
                return hasDateRange
                    ? { displayHeaders: ['No.', 'RUTA', 'TIE', 'FECHA DE ASIGNACIÓN', 'CLIENTE'], dataKeys: ['index', 'route', 'tie', 'assignedAt', 'name'] }
                    : { displayHeaders: ['No.', 'RUTA', 'TIE', 'CLIENTE'], dataKeys: ['index', 'route', 'tie', 'name'] };
            case 'entity':
                return hasDateRange
                    ? { displayHeaders: ['No.', 'ENTIDAD', 'TIE', 'FECHA DE ASIGNACIÓN', 'CLIENTE'], dataKeys: ['index', 'entity', 'tie', 'assignedAt', 'name'] }
                    : { displayHeaders: ['No.', 'ENTIDAD', 'TIE', 'CLIENTE'], dataKeys: ['index', 'entity', 'tie', 'name'] };
            case 'isr_affected':
                return hasDateRange
                    ? { displayHeaders: ['No.', 'CLIENTES', 'ENTIDAD', 'FECHA DE CAMBIO', 'ESTATUS'], dataKeys: ['index', 'name', 'entity', 'changedAt', 'status'] }
                    : { displayHeaders: ['No.', 'CLIENTES', 'ENTIDAD', 'ESTATUS'], dataKeys: ['index', 'name', 'entity', 'status'] };
            case 'bank_account':
                return hasDateRange
                    ? {
                        displayHeaders: ['No.', 'CLIENTE', 'ENTIDAD', 'No. DE CUENTA ANTERIOR', 'No. DE CUENTA', 'FECHA DE CAMBIO'],
                        dataKeys: ['index', 'name', 'entity', 'previousAccountNumber', 'accountNumber', 'changedAt']
                    }
                    : {
                        displayHeaders: ['No.', 'CLIENTE', 'ENTIDAD', 'No. DE CUENTA ANTERIOR', 'No. DE CUENTA'],
                        dataKeys: ['index', 'name', 'entity', 'previousAccountNumber', 'accountNumber']
                    };
            case 'owner_change':
                return {
                    displayHeaders: ['No.', 'CLIENTE ANTERIOR', 'CLIENTE ACTUAL', 'FECHA DEL CAMBIO', 'ENTIDAD', 'TIE', 'RUTA'],
                    dataKeys: ['index', 'previousOwner', 'currentOwner', 'changedAt', 'entity', 'tie', 'route']
                };
            case 'nit':
            default:
                return { displayHeaders: ['No.', 'NIT', 'CLIENTE', 'ENTIDAD'], dataKeys: ['index', 'nit', 'name', 'entity'] };
        }
    }

    private getBusLifecycleReportHeaders(reportType: BusLifecycleReportOption['value'], rows: any[], hasDateRange: boolean): { displayHeaders: string[]; dataKeys: string[] } {
        switch(reportType) {
            case 'activos':
                const weekKeys = Array.from(new Set(
                    (Array.isArray(rows) ? rows : [])
                        .flatMap(row => Object.keys(row || {}).filter(key => key.startsWith('week_')))
                )).sort((a, b) => {
                    const weekA = Number(a.replace('week_', '')) || 0;
                    const weekB = Number(b.replace('week_', '')) || 0;
                    return weekA - weekB;
                });
                return {
                    displayHeaders: ['No.', 'ENTIDAD', 'RUTA', ...weekKeys.map(key => `TOTAL DE TIE SEMANA ${key.replace('week_', '')}`)],
                    dataKeys: ['index', 'entity', 'route', ...weekKeys]
                };
            case 'altas':
                return hasDateRange
                    ? {
                        displayHeaders: ['No.', 'ENTIDAD', 'TIE', 'FECHA'],
                        dataKeys: ['index', 'entity', 'tie', 'date']
                    }
                    : {
                        displayHeaders: ['No.', 'ENTIDAD', 'TIE'],
                        dataKeys: ['index', 'entity', 'tie']
                    };
            case 'inactivo':
                return hasDateRange
                    ? {
                        displayHeaders: ['No.', 'FECHA', 'ENTIDAD', 'TIE', 'RUTA'],
                        dataKeys: ['index', 'date', 'entity', 'tie', 'route']
                    }
                    : {
                        displayHeaders: ['No.', 'ENTIDAD', 'TIE', 'RUTA'],
                        dataKeys: ['index', 'entity', 'tie', 'route']
                    };
            case 'traslados':
            default:
                return {
                    displayHeaders: ['No.', 'FECHA', 'ENTIDAD ANTERIOR', 'PLACA', 'TIE', 'NUEVA ENTIDAD'],
                    dataKeys: ['index', 'date', 'previousEntity', 'plate', 'tie', 'newEntity']
                };
        }
    }

    private formatDate(dateValue: Date): string {
        const date = new Date(dateValue);
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
    }

    private cleanFileName(value: string): string {
        return value
            .trim()
            .replace(/\s+/g, '_')
            .replace(/[^a-zA-Z0-9_\-]/g, '');
    }

    private cleanColumnName(value: string): string {
        return String(value || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
            .toLowerCase();
    }
}
